import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Textarea,
  Switch,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Link,
  Code,
  Divider,
  Collapse,
  useDisclosure as useCollapseDisclosure,
} from '@chakra-ui/react'
import { FiCheck, FiAlertTriangle, FiExternalLink, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { getExplorerAddressUrl } from '@/constants/chains'
import {
  contractService,
  type LifecycleBatchTriggerResult,
} from '@/services/admin/contractService'
import { LifecycleBatchItemResults } from './LifecycleBatchItemResults'
import {
  getTriggerProgressStatus,
  ProgressModal,
  type ProgressStatus,
} from '@/components/common/ProgressModal'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import { useApiError } from '@/hooks/useApiError'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import { buildContractChainOptions } from './contractChainOptions'

// Verification result interface
interface VerificationResult extends LifecycleBatchTriggerResult {
  details?: {
    successful_verifications?: Array<{
      deployment_id: string
      contract_address: string
      chain_id: number
      verification_status: string
    }>
    failed_verifications?: Array<{
      deployment_id: string
      error: string
      details?: any
    }>
  }
}

export const ContractVerificationTrigger: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const warningBg = useColorModeValue('orange.50', 'orange.900')
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()
  const { isOpen: isProgressOpen, onOpen: onProgressOpen, onClose: onProgressClose } = useDisclosure()

  const [orderIds, setOrderIds] = useState('')
  const [chainId, setChainId] = useState('')
  const [limit, setLimit] = useState(10)
  const [forceVerification, setForceVerification] = useState(false)
  const [orderIdsError, setOrderIdsError] = useState('')
  const [results, setResults] = useState<VerificationResult | null>(null)
  const { isOpen: isDetailsOpen, onToggle: onToggleDetails } = useCollapseDisclosure()
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle')
  const [progressCurrent, setProgressCurrent] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const chainOptions = useMemo(() => buildContractChainOptions(currentEnv), [currentEnv])
  const chainOptionMap = useMemo(
    () => new Map(chainOptions.map(option => [option.value, option])),
    [chainOptions]
  )
  const chainLabelByNumericId = useMemo(
    () => new Map(chainOptions.map(option => [option.chainId, option.label])),
    [chainOptions]
  )
  const selectedChainId = chainId
    ? chainOptionMap.get(chainId)?.chainId ?? 'unknown'
    : null

  const executeVerification = async () => {
    const orderIdList = parseMultilineEntries(orderIds)

    const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
    if (invalidIds.length > 0) {
      throw new Error(`Invalid order IDs: ${invalidIds.join(', ')}`)
    }

    const params: any = {
      limit,
      force: forceVerification
    }

    if (orderIdList.length > 0) {
      params.orderIds = orderIdList
    }

    if (chainId) {
      params.chainId = chainId
    }

    setProgressTotal(limit)
    setProgressCurrent(0)
    setProgressMessage(t('monitoring.contractVerification.progress.preparing'))
    setProgressStatus('processing')
    onProgressOpen()

    return await contractService.triggerContractVerification(params, currentEnv)
  }

  const mutation = useMutation({
    mutationFn: executeVerification,
    onSuccess: (response) => {
      if (response) {
        setResults(response)
        const { queued, alreadyQueued, skipped, failed, total } = response
        setProgressTotal(total)
        setProgressCurrent(total)
        setProgressStatus(getTriggerProgressStatus(failed))
        setProgressMessage(t('monitoring.contractVerification.progress.completed'))
        
        // Add to operation history if available
        if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
          (window as any).addOperationHistory({
            type: 'contractVerification',
            operator: 'Admin',
            result: failed > 0 ? 'partial' : 'success',
            details: `Queued: ${queued}, Already Queued: ${alreadyQueued}, Skipped: ${skipped}, Failed: ${failed}`,
            metadata: { queued, alreadyQueued, skipped, failed }
          })
        }
        
        const toastMethod = failed > 0
          ? toast.warning
          : queued > 0
          ? toast.success
          : toast.info
        
        toastMethod({
          title: t('monitoring.contractVerification.success.title'),
          description: t('monitoring.contractVerification.success.description', {
            queued: queued ?? 0,
            alreadyQueued: alreadyQueued ?? 0,
            skipped: skipped ?? 0,
            failed: failed ?? 0
          }),
          duration: 5000,
          isClosable: true,
          id: 'contract-verification-success',
        })
      }
    },
    onError: (error: any) => {
      setProgressStatus('failed')
      const errorMessage = error.response?.data?.error || error.message || 'Failed to trigger contract verification'
      setProgressMessage(errorMessage)
      
      // Add to operation history if available
      if (typeof window !== 'undefined' && (window as any).addOperationHistory) {
        (window as any).addOperationHistory({
          type: 'contractVerification',
          operator: 'Admin',
          result: 'failed',
          details: errorMessage,
          errorCode: error.response?.status
        })
      }
      
      handleError(error, 'Contract verification')
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Contract Verification Error Details:', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        })
      }
    },
  })

  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setOrderIds(value)
    
    if (value) {
      const orderIdList = parseMultilineEntries(value)
      const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
      if (invalidIds.length > 0) {
        setOrderIdsError(`Invalid order IDs: ${invalidIds.slice(0, 3).join(', ')}`)
      } else {
        setOrderIdsError('')
      }
    } else {
      setOrderIdsError('')
    }
  }

  const handleSubmit = () => {
    if (orderIdsError) {
      return
    }
    
    // Check if force verification is enabled and show confirmation dialog
    if (forceVerification) {
      onConfirmOpen()
    } else {
      mutation.mutate()
    }
  }

  const handleConfirmVerification = () => {
    onConfirmClose()
    mutation.mutate()
  }

  const getStatColor = (value: number, type: 'queued' | 'alreadyQueued' | 'skipped' | 'failed') => {
    if (value === 0) return 'gray'
    switch (type) {
      case 'queued':
        return 'green'
      case 'alreadyQueued':
        return 'blue'
      case 'skipped':
        return 'yellow'
      case 'failed':
        return 'red'
      default:
        return 'gray'
    }
  }

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        {t('monitoring.contractVerification.description')}
      </Alert>

      <Box>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!orderIdsError}>
            <FormLabel>{t('monitoring.contractVerification.fields.orderIds.label')}</FormLabel>
            <Textarea
              placeholder={t('monitoring.contractVerification.fields.orderIds.placeholder')}
              value={orderIds}
              onChange={handleOrderIdsChange}
              rows={4}
              isDisabled={mutation.isPending}
            />
            {!orderIdsError && (
              <FormHelperText>
                {t('monitoring.contractVerification.fields.orderIds.helper')}
              </FormHelperText>
            )}
            <FormErrorMessage>{orderIdsError}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractVerification.fields.chainId.label')}</FormLabel>
            <DropdownSelect
              placeholder={t('monitoring.contractVerification.fields.chainId.placeholder')}
              emptyOptionLabel={t('monitoring.contractVerification.fields.chainId.placeholder')}
              value={chainId}
              options={chainOptions}
              onChange={setChainId}
              ariaLabel={t('monitoring.contractVerification.fields.chainId.label')}
              isDisabled={mutation.isPending}
            />
            <FormHelperText>
              {t('monitoring.contractVerification.fields.chainId.helper')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractVerification.fields.limit.label')}</FormLabel>
            <NumberInput
              value={limit}
              onChange={(_, value) => setLimit(value || 10)}
              min={1}
              max={100}
              isDisabled={mutation.isPending}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              {t('monitoring.contractVerification.fields.limit.helper')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <HStack justify="space-between" align="center">
              <FormLabel htmlFor="force-verification" mb="0">
                {t('monitoring.contractVerification.fields.forceVerification.label')}
              </FormLabel>
              <Switch
                id="force-verification"
                isChecked={forceVerification}
                onChange={(e) => setForceVerification(e.target.checked)}
                isDisabled={mutation.isPending}
                colorScheme="orange"
              />
            </HStack>
            {forceVerification && (
              <FormHelperText mt={2}>
                {t('monitoring.contractVerification.fields.forceVerification.helper')}
              </FormHelperText>
            )}
          </FormControl>
          {forceVerification && (
            <Alert 
              status="warning" 
              borderRadius="md"
              bg={warningBg}
            >
              <AlertIcon as={FiAlertTriangle} />
              <Box>
                <AlertDescription>
                  {t('monitoring.contractVerification.fields.forceVerification.warning')}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          <Button
            colorScheme="blue"
            leftIcon={<FiCheck />}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingText={t('monitoring.contractVerification.button.loading')}
            isDisabled={mutation.isPending || !!orderIdsError}
          >
            {t('monitoring.contractVerification.button.submit')}
          </Button>
          
          {mutation.isPending && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Processing Verification</AlertTitle>
                <AlertDescription>
                  <Text fontSize="sm">
                    This operation may take up to 30 seconds depending on the number of contracts.
                  </Text>
                  <Text fontSize="sm" mt={1}>
                    Each contract verification will be queued for background processing.
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </Box>

      {results && (
        <Box
          bg={bgColor}
          p={6}
          borderRadius="md"
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={4} align="stretch">
            <Box fontWeight="semibold" fontSize="lg">
              {t('monitoring.contractVerification.results.title')}
            </Box>
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractVerification.results.total')}
                </StatLabel>
                <StatNumber>{results.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractVerification.results.queued')}</span>
                    <Badge colorScheme={getStatColor(results.queued, 'queued')} size="sm">
                      {results.queued > 0 ? 'Queued' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.queued, 'queued')}.500`}>
                  {results.queued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.queued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractVerification.results.alreadyQueued')}</span>
                    <Badge colorScheme={getStatColor(results.alreadyQueued, 'alreadyQueued')} size="sm">
                      {results.alreadyQueued > 0 ? 'Already In Queue' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.alreadyQueued, 'alreadyQueued')}.500`}>
                  {results.alreadyQueued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.alreadyQueued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractVerification.results.skipped')}</span>
                    <Badge colorScheme={getStatColor(results.skipped, 'skipped')} size="sm">
                      {results.skipped > 0 ? 'Skipped' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.skipped, 'skipped')}.500`}>
                  {results.skipped}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.skipped / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.contractVerification.results.failed')}</span>
                    <Badge colorScheme={getStatColor(results.failed, 'failed')} size="sm">
                      {results.failed > 0 ? 'Failed' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.failed, 'failed')}.500`}>
                  {results.failed}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.failed / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
            </SimpleGrid>

            <LifecycleBatchItemResults items={results.items} />
            
            {/* Detailed Results Toggle */}
            {((results as any).details?.successful_verifications?.length > 0 || (results as any).details?.failed_verifications?.length > 0) && (
              <>
                <Divider />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleDetails}
                  leftIcon={isDetailsOpen ? <FiChevronDown /> : <FiChevronRight />}
                >
                  {isDetailsOpen ? 'Hide' : 'Show'} Verification Details
                </Button>
                
                <Collapse in={isDetailsOpen}>
                  <VStack spacing={4} align="stretch">
                    
                    {/* Successful Verifications */}
                    {results.details?.successful_verifications && results.details.successful_verifications.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" color="green.500" mb={2}>
                          Successful Verifications ({results.details.successful_verifications.length})
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {results.details.successful_verifications.map((verification, index) => {
                            const explorerUrl = getExplorerAddressUrl(
                              verification.chain_id,
                              verification.contract_address
                            )
                            const chainLabel = chainLabelByNumericId.get(verification.chain_id)
                            return (
                              <Box key={index} p={3} borderWidth={1} borderColor="green.200" borderRadius="md" bg="green.50">
                                <HStack justify="space-between" wrap="wrap">
                                  <VStack align="start" spacing={1} fontSize="sm">
                                    <HStack>
                                      <Badge variant="outline">{chainLabel || `Chain ${verification.chain_id}`}</Badge>
                                      <Code fontSize="xs">{verification.deployment_id}</Code>
                                    </HStack>
                                    <Text color="gray.600">
                                      Status: {verification.verification_status}
                                    </Text>
                                  </VStack>
                                  {explorerUrl && (
                                    <Link href={explorerUrl} isExternal color="blue.500" fontSize="sm">
                                      View Address <FiExternalLink style={{ display: 'inline' }} />
                                    </Link>
                                  )}
                                </HStack>
                              </Box>
                            )
                          })}
                        </VStack>
                      </Box>
                    )}
                    
                    {/* Failed Verifications */}
                    {results.details?.failed_verifications && results.details.failed_verifications.length > 0 && (
                      <Box>
                        <Text fontWeight="medium" color="red.500" mb={2}>
                          Failed Verifications ({results.details.failed_verifications.length})
                        </Text>
                        <VStack spacing={2} align="stretch">
                          {results.details.failed_verifications.map((failure, index) => (
                            <Box key={index} p={3} borderWidth={1} borderColor="red.200" borderRadius="md" bg="red.50">
                              <VStack align="stretch" spacing={2} fontSize="sm">
                                <HStack>
                                  <Code fontSize="xs">{failure.deployment_id}</Code>
                                </HStack>
                                <Text color="red.600" fontWeight="medium">
                                  {failure.error}
                                </Text>
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Collapse>
              </>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <>
                <Divider />
                <Text fontSize="sm" color="gray.500">
                  Debug: {selectedChainId && `Chain: ${selectedChainId}`} |
                  {orderIds
                    ? `${orderIds.split('\n').filter(id => id.trim()).length} specific IDs`
                    : 'All contracts'} |
                  Limit: {limit}
                </Text>
              </>
            )}
          </VStack>
        </Box>
      )}

      {/* Force Verification Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('confirmDialog.forceVerification.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('confirmDialog.forceVerification.message')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                {t('confirmDialog.forceVerification.cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleConfirmVerification} ml={3}>
                {t('confirmDialog.forceVerification.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Progress Modal */}
      <ProgressModal
        isOpen={isProgressOpen}
        onClose={onProgressClose}
        title={t('monitoring.contractVerification.progress.title')}
        total={progressTotal}
        current={progressCurrent}
        status={progressStatus}
        message={progressMessage}
        results={results ? {
          queued: results.queued,
          alreadyQueued: results.alreadyQueued,
          failed: results.failed,
          skipped: results.skipped
        } : undefined}
      />
    </VStack>
  )
}
